<script setup>
  import { ref, computed, onMounted, watchEffect } from 'vue';
  import { useWindAlertStore } from '@/stores/WindAlertStore';
  import BaseModal from './base/BaseModal.vue';
  import Toggle from './controls/Toggle.vue';
  import Icon from '@/components/icons/Icon.vue';
  import ModalWindAlertManageDetailViewTriggerOn from './ModalWindAlertManageDetailViewTriggerOn.vue';
  import ModalWindAlertManageDetailViewFrequency from './ModalWindAlertManageDetailViewFrequency.vue';
  import ModalWindAlertManageDetailViewModelRange from './ModalWindAlertManageDetailViewModelRange.vue';
  import ModalWindAlertManageDetailViewDay from './ModalWindAlertManageDetailViewDay.vue';
  import ModalWindAlertManageDetailViewForecastModels from './ModalWindAlertManageDetailViewForecastModels.vue';
  import ModalWindAlertManageDetailViewHistory from './ModalWindAlertManageDetailViewHistory.vue';
  import AlertDevicesManageContent from './AlertDevicesManageContent.vue';
  import Checkbox from '@/components/controls/Checkbox.vue';
  import WindDirectionSelector from '@/components/controls/WindDirectionSelector.vue';
  import WindDirectionMapContainer from '@/components/controls/WindDirectionMapContainer.vue';
  import { useApiForecastModels } from '@/composables/useApiForecastModels';
  import { sortForecastModels } from '@/utils/sortForecastModels';
  import useModalNavigation from '@/composables/useModalNavigation';
  import RangeSliderDual from './controls/RangeSliderDual.vue';
  import windInfo from '@/data/windInfo.json';

  const windAlertStore = useWindAlertStore();
  const apiForecastModels = useApiForecastModels();

  const emits = defineEmits(['close']);

  const props = defineProps({
    show: {
      type: Boolean,
      default: false
    },
    alert: {
      type: Object,
      default: null
    },
    spotId: {
      type: [String, Number],
      default: null
    },
    spotName: {
      type: String,
      default: ''
    }
  });

  const isEditMode = computed(() => !!props.alert);
  const historyAlertId = computed(() => props.alert?.id ?? props.alert?.alert_id ?? null);
  const actionLabel = computed(() => isEditMode.value ? 'Save' : 'Add Alert');
  const errorCount = computed(() => Object.keys(validationErrors.value).length);
  const headerTitle = computed(() => {
    const action = isEditMode.value ? 'Edit Alert' : 'Add Alert';
    return props.spotName ? `${action} for ${props.spotName}` : action;
  });

  const devicesValid = computed(() => selectedDeviceIds.value.length > 0);
  const sendOnValid = computed(() => sendOnObservations.value || sendOnForecast.value);
  const validationErrors = ref({});

  function validateForm() {
    const errors = {};
    if (!devicesValid.value) {
      errors.devices = 'At least one device is required';
    }
    if (!sendOnValid.value) {
      errors.sendOn = 'Select Observations, Forecast, or both';
    }
    validationErrors.value = errors;
    return Object.keys(errors).length === 0;
  }

  // --- Form state ---
  const enabled = ref(true);
  const sendOnObservations = ref(true);
  const sendOnForecast = ref(false);
  const windDirections = ref([...windInfo.cardinalDirections]);
  const triggerOn = ref('average');
  const frequency = ref(60);
  const selectedDays = ref(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']);
  const startHour = ref(0);
  const endHour = ref(24);
  const minThreshold = ref(0);
  const maxThreshold = ref(51);
  const modelRange = ref('short');
  const availableModels = ref([]);
  const selectedModelIds = ref([]);
  const modelsLoading = ref(false);
  const selectedDeviceIds = ref([]);

  const availableDevices = computed(() => windAlertStore.alertDevices || []);
  const websiteDevices = computed(() => availableDevices.value.filter(d => d.type === 'E-Mail')); // 'Mobile' (text messages) is not supported at this time
  const appDevices = computed(() => availableDevices.value.filter(d => d.type === 'iPhone' || d.type === 'Android'));
  const enabledDevicesCount = computed(() => availableDevices.value.filter(d => d.enabled !== false).length);
  const totalDevicesCount = computed(() => availableDevices.value.length);

  function formatPhone(raw) {
    const digits = String(raw || '').replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) return raw;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  function deviceSubText(device) {
    if (device.type === 'E-Mail') {
      const addresses = Array.isArray(device.address)
        ? device.address
        : String(device.address || '').split(',').map(a => a.trim()).filter(Boolean);
      const count = addresses.length;
      if (count === 1) return addresses[0];
      return `${count} email addresses`;
    }
    if (device.type === 'Mobile') {
      const phoneRaw = (device.address || '').split('@')[0];
      return `${device.name || ''} - ${formatPhone(phoneRaw)}`;
    }
    return '';
  }

  const triggerOnLabels = {
    average: 'Average',
    gust: 'Gust'
  };

  const frequencyLabels = {
    30: 'Every 30 Mins',
    60: 'Every 60 Mins',
    120: 'Every 2 Hours',
    360: 'Every 6 Hours',
    720: 'Every 12 Hours',
    1440: 'Once a Day'
  };

  const modelRangeLabels = {
    short: 'Short Range',
    long: 'Long Range'
  };

  const allDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const windDirectionOrder = windInfo.cardinalDirections;

  function formatHour(h) {
    if (h === 0 || h === 24) return '12:00A';
    if (h === 12) return '12:00P';
    return h < 12 ? `${h}:00A` : `${h - 12}:00P`;
  }

  function formatTime(h) {
    const hour = h === 24 ? 23 : h;
    const mins = h === 24 ? 59 : 0;
    const secs = h === 24 ? 59 : 0;
    return `${String(hour).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  const timeRangeLabel = computed(() => `${formatHour(startHour.value)} to ${formatHour(endHour.value)}`);

  const selectedModelsLabel = computed(() => {
    if (selectedModelIds.value.length === 0) return 'None';
    if (selectedModelIds.value.length === availableModels.value.length) return 'All';
    return selectedModelIds.value
      .map(id => availableModels.value.find(m => m.model_id === id)?.model_short_name)
      .filter(Boolean)
      .join(', ');
  });

  async function fetchForecastModels(spotId) {
    if (!spotId) return;

    modelsLoading.value = true;

    const response = await apiForecastModels.bySpot({ spotId });
    const { data, error } = response || {};

    if (error?.value) {
      console.error('Error fetching forecast models', error);
    } else if (data?.value?.model_summary) {
      availableModels.value = sortForecastModels(data.value.model_summary);
    }

    modelsLoading.value = false;

    if (selectedModelIds.value.length === 0) {
      const blendModel = availableModels.value.find(m => m.model_id === -1);
      if (blendModel) {
        selectedModelIds.value = [-1];
      }
    }
  }

  const selectedDaysLabel = computed(() => {
    if (selectedDays.value.length === 7) return 'Every Day';
    if (selectedDays.value.length === 0) return 'None';
    return selectedDays.value.join(', ');
  });

  // --- Modal navigation ---
  const showModal = ref(false);
  const {
    activeView,
    transitionName,
    modalBody,
    activeViewTitle,
    pushView,
    popView,
    onAfterEnter,
  } = useModalNavigation({
    triggerOn: 'Trigger On',
    frequency: 'Frequency',
    modelRange: 'Model Range',
    forecastModels: 'Forecast Models',
    day: 'Day',
    history: 'Recent Alerts',
    devices: 'Devices',
  });

  const devicesContentRef = ref(null);
  const devicesViewActive = computed(() => activeView.value === 'devices');
  const devicesContentTitle = computed(() => devicesContentRef.value?.title ?? 'Devices');
  const devicesContentSubmitLabel = computed(() => devicesContentRef.value?.submitLabel ?? '');
  const devicesContentErrorCount = computed(() => devicesContentRef.value?.errorCount ?? 0);
  const devicesContentAtRoot = computed(() => devicesContentRef.value?.isAtRoot ?? true);

  function handleHeaderBack() {
    if (devicesViewActive.value && !devicesContentAtRoot.value) {
      devicesContentRef.value?.back();
      return;
    }
    popView();
  }

  async function handleDevicesSubmit() {
    await devicesContentRef.value?.submit();
  }

  function parseHour(value, fallback) {
    if (value == null) return fallback;
    if (typeof value === 'number') return value;
    const match = String(value).match(/^(\d{1,2}):(\d{2})/);
    if (!match) return fallback;
    const h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    if (Number.isNaN(h)) return fallback;
    if (h === 23 && m >= 59) return 24;
    return h;
  }

  function toArray(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') return value.split(',').map(v => v.trim()).filter(Boolean);
    return [];
  }

  function populateFromAlert(alert) {
    if (!alert) return;

    enabled.value = alert.enabled ?? true;
    sendOnObservations.value = alert.frequency != null && alert.frequency !== '';
    sendOnForecast.value = toArray(alert.model_ids).length > 0;

    const directionsRaw = toArray(alert.wind_directions || alert.directions);
    windDirections.value = directionsRaw.length
      ? directionsRaw.map(d => {
          const n = typeof d === 'number' ? d : parseInt(d, 10);
          return Number.isNaN(n) ? d : windDirectionOrder[n];
        }).filter(Boolean)
      : [...windInfo.cardinalDirections];

    triggerOn.value = (alert.trigger || 'average').toLowerCase();
    frequency.value = alert.frequency || 60;
    startHour.value = parseHour(alert.start_time ?? alert.start_hour, 0);
    endHour.value = parseHour(alert.end_time ?? alert.end_hour, 24);
    minThreshold.value = alert.min_threshold || 0;
    maxThreshold.value = alert.max_threshold || 51;
    modelRange.value = alert.is_extended_fx ? 'long' : (alert.model_range || 'short');

    const daysRaw = toArray(alert.days);
    if (daysRaw.length) {
      selectedDays.value = daysRaw.map(d => {
        const n = typeof d === 'number' ? d : parseInt(d, 10);
        return Number.isNaN(n) ? d : allDays[n];
      }).filter(Boolean);
    }

    const modelIdsRaw = toArray(alert.model_ids);
    if (modelIdsRaw.length) {
      selectedModelIds.value = modelIdsRaw.map(id => typeof id === 'number' ? id : parseInt(id, 10)).filter(n => !Number.isNaN(n));
    }

    const deviceIdsRaw = toArray(alert.alert_devices);
    if (deviceIdsRaw.length) {
      selectedDeviceIds.value = deviceIdsRaw.map(id => {
        if (typeof id === 'number') return id;
        if (typeof id === 'object' && id !== null) return id.id;
        return parseInt(id, 10);
      }).filter(n => n != null && !Number.isNaN(n));
    }
  }

  function resetForm() {
    validationErrors.value = {};
    enabled.value = true;
    sendOnObservations.value = true;
    sendOnForecast.value = false;
    windDirections.value = [...windInfo.cardinalDirections];
    triggerOn.value = 'average';
    frequency.value = 60;
    startHour.value = 0;
    endHour.value = 24;
    minThreshold.value = 0;
    maxThreshold.value = 51;
    selectedDays.value = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    modelRange.value = 'short';
    selectedModelIds.value = [];
    selectedDeviceIds.value = [];
  }

  function closeModal() {
    showModal.value = false;
    activeView.value = null;
    emits('close');
  }

  async function handleSubmit() {
    if (!validateForm()) return;

    const alertData = {
      spot_id: props.spotId,
      enabled: enabled.value,
      is_obs: sendOnObservations.value,
      trigger: triggerOn.value.charAt(0).toUpperCase() + triggerOn.value.slice(1),
      frequency: sendOnObservations.value ? frequency.value : '',
      days: selectedDays.value.map(d => allDays.indexOf(d)).filter(i => i !== -1).join(','),
      directions: windDirections.value.map(d => windDirectionOrder.indexOf(d)).filter(i => i !== -1).join(','),
      start_time: formatTime(startHour.value),
      end_time: formatTime(endHour.value),
      min_threshold: minThreshold.value,
      max_threshold: maxThreshold.value,
      model_ids: sendOnForecast.value ? selectedModelIds.value.join(',') : '',
      is_extended_fx: modelRange.value === 'long',
      alert_devices: selectedDeviceIds.value.join(','),
    };

    const wasEdit = isEditMode.value;
    if (wasEdit) {
      const { spot_id, ...updateData } = alertData;
      await windAlertStore.updateAlert({ alert_id: parseInt(props.alert.id, 10), ...updateData });
    } else {
      await windAlertStore.addAlert({ ...alertData });
    }

    await windAlertStore.getWindAlerts(undefined, true);

    if (jwc) {
      const action = wasEdit ? 'Saved' : 'Created';
      const target = props.spotName ? `alert for <strong>${props.spotName}</strong>` : 'alert';
      jwc.toaster.toast({
        type: 'success',
        content: `${action} ${target}`,
      });
    }

    closeModal();
  }

  onMounted(() => {
    watchEffect(() => {
      showModal.value = props.show;

      if (props.show) {
        if (props.alert) {
          populateFromAlert(props.alert);
        } else {
          resetForm();
        }

        fetchForecastModels(props.spotId);

        if (!windAlertStore.alertDevices) {
          windAlertStore.getAlertDevices();
        }
      }
    });
  });
</script>

<template>
  <BaseModal
    v-model="showModal"
    :allowOutsideClick="true"
    height="tall"
    @close="closeModal"
  >
    <header class="jwx-modal__header">
      <h2 class="jwx-modal__title">
        <button v-if="activeView" type="button" class="jwx-modal__back-button" @click="handleHeaderBack">
          <Icon name="chevron-left" />
        </button>
        {{ devicesViewActive ? devicesContentTitle : (activeView ? activeViewTitle : headerTitle) }}
      </h2>
    </header>

    <div ref="modalBody" class="jwx-modal__body">
      <Transition :name="transitionName" mode="out-in" @after-enter="onAfterEnter">

        <!-- Main view -->
        <div v-if="!activeView" key="main">

          <!-- Section 1: Enabled -->
          <div class="jwx-modal__section">
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row">
                  <div class="jwx-table-view__row-content">
                    <label for="alert-enabled" class="jwx-table-view__row-label jwx-font-weight-400">Enabled</label>
                    <div class="jwx-table-view__row-actions">
                      <Toggle
                        v-model="enabled"
                        name="alert-enabled"
                        id="alert-enabled"
                        :surface="0"
                      />
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <!-- Devices -->
          <div class="jwx-modal__section">
            <div class="jwx-modal__section-header">
              <h3 class="jwx-modal__section-title">Devices</h3>
              <div class="jwx-modal__section-actions">
                <span class="jwx-modal__control-required" :data-valid="devicesValid ? 'true' : null">Required</span>
              </div>
            </div>
            <div class="jwx-modal__section-body">
              <template v-if="availableDevices.length > 0">
                <ul v-if="websiteDevices.length" class="jwx-block jwx-table-view" data-surface="1">
                  <li v-for="device in websiteDevices" :key="device.id" class="jwx-table-view__row">
                    <div class="jwx-table-view__row-content">
                      <div class="jwx-table-view__row-main-content">
                        <label :for="`alert-device-${device.id}`" class="jwx-table-view__row-label jwx-font-weight-400">{{ device.type === 'Mobile' ? 'Text Message' : (device.name || device.type) }}</label>
                        <p v-if="deviceSubText(device)" class="jwx-table-view__row-text">{{ deviceSubText(device) }}</p>
                      </div>
                      <div class="jwx-table-view__row-actions">
                        <span
                          v-if="device.enabled === false"
                          class="jwx-alert-row__device-disabled-icon"
                          title="Device is disabled"
                        >
                          <Icon name="exclamation-circle" />
                        </span>
                        <Checkbox
                          v-else
                          :id="`alert-device-${device.id}`"
                          :value="device.id"
                          :textLabel="false"
                          :surface="0"
                          v-model="selectedDeviceIds"
                        />
                      </div>
                    </div>
                  </li>
                </ul>
                <ul v-if="appDevices.length" class="jwx-block jwx-table-view" data-surface="1">
                  <li v-for="device in appDevices" :key="device.id" class="jwx-table-view__row">
                    <div class="jwx-table-view__row-content">
                      <label :for="`alert-device-${device.id}`" class="jwx-table-view__row-label jwx-font-weight-400">{{ device.type === 'iPhone' ? 'iOS' : (device.name || device.type) }}</label>
                      <div class="jwx-table-view__row-actions">
                        <span
                          v-if="device.enabled === false"
                          class="jwx-alert-row__device-disabled-icon"
                          title="Device is disabled"
                        >
                          <Icon name="exclamation-circle" />
                        </span>
                        <Checkbox
                          v-else
                          :id="`alert-device-${device.id}`"
                          :value="device.id"
                          :textLabel="false"
                          :surface="0"
                          v-model="selectedDeviceIds"
                        />
                      </div>
                    </div>
                  </li>
                </ul>
                <ul class="jwx-block jwx-table-view" data-surface="1">
                  <li class="jwx-table-view__row">
                    <button type="button" class="jwx-table-view__action-row" @click="pushView('devices')">
                      <p class="jwx-table-view__row-label jwx-font-weight-400">Manage Devices</p>
                      <div class="jwx-table-view__action-row-detail">
                        <span class="jwx-chip jwx-chip--neutral">
                          <span class="jwx-chip__text">{{ enabledDevicesCount }} / {{ totalDevicesCount }}</span>
                        </span>
                        <Icon name="chevron-right" />
                      </div>
                    </button>
                  </li>
                </ul>
              </template>
              <div v-else class="jwx-block jwx-grid jwx-place-content-center jwx-p-xxxl" data-surface="1">
                <p class="jwx-modal__section-description jwx-m-bottom-lg">
                  You have no devices configured.
                </p>
                <button
                  type="button"
                  class="jwx-button jwx-button--pill"
                  @click.prevent="pushView('devices')"
                  data-surface="0"
                >
                  Setup a Device
                </button>
              </div>
              <p v-if="validationErrors.devices" class="jwx-modal__control-error">{{ validationErrors.devices }}</p>
            </div>
          </div>

          <!-- Section 2: Send On -->
          <div class="jwx-modal__section">
            <div class="jwx-modal__section-header">
              <h3 class="jwx-modal__section-title">Send On</h3>
              <div class="jwx-modal__section-actions">
                <span class="jwx-modal__control-required" :data-valid="sendOnValid ? 'true' : null">Required</span>
              </div>
            </div>
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row">
                  <div class="jwx-table-view__row-content">
                    <label for="send-on-observations" class="jwx-table-view__row-label jwx-font-weight-400">Observations</label>
                    <div class="jwx-table-view__row-actions">
                      <Toggle
                        v-model="sendOnObservations"
                        name="send-on-observations"
                        id="send-on-observations"
                        :surface="0"
                      />
                    </div>
                  </div>
                </li>
                <li class="jwx-table-view__row">
                  <div class="jwx-table-view__row-content">
                    <label for="send-on-forecast" class="jwx-table-view__row-label jwx-font-weight-400">Forecast</label>
                    <div class="jwx-table-view__row-actions">
                      <Toggle
                        v-model="sendOnForecast"
                        name="send-on-forecast"
                        id="send-on-forecast"
                        :surface="0"
                      />
                    </div>
                  </div>
                </li>
              </ul>
              <p v-if="validationErrors.sendOn" class="jwx-modal__control-error">{{ validationErrors.sendOn }}</p>
              <p class="jwx-modal__section-description">Alerts will be sent on observed and/or forecasted values that fall within the wind thresholds, forecast, and delivery settings.</p>
            </div>
          </div>

          <!-- Wind Direction -->
          <div class="jwx-modal__section">
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row" data-multi-row>
                  <div class="jwx-table-view__row-content">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Wind Direction</p>
                  </div>
                  <div class="jwx-table-view__row-content jwx-p-block-xxl">
                    <WindDirectionMapContainer>
                      <WindDirectionSelector v-model="windDirections" />
                    </WindDirectionMapContainer>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <!-- Threshold -->
          <div class="jwx-modal__section">
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row" data-multi-row>
                  <div class="jwx-table-view__row-content">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Threshold</p>
                    <span class="jwx-table-view__row-value">{{ minThreshold }} mph to {{ maxThreshold  === 51 ? 'Any' : maxThreshold }} mph</span>
                  </div>
                  <div class="jwx-table-view__row-content">
                    <RangeSliderDual
                      id="wind-alert-threshold-slider"
                      :min="0"
                      :max="51"
                      v-model:minModel="minThreshold"
                      v-model:maxModel="maxThreshold"
                    />
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <!-- Trigger On -->
          <div class="jwx-modal__section">
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row">
                  <button type="button" class="jwx-table-view__action-row" @click="pushView('triggerOn')">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Trigger On</p>
                    <div class="jwx-table-view__action-row-detail">
                      <span class="jwx-table-view__row-value">{{ triggerOnLabels[triggerOn] }}</span>
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <!-- Section 4: Delivery Settings -->
          <div class="jwx-modal__section">
            <div class="jwx-modal__section-header">
              <h3 class="jwx-modal__section-title">Delivery Settings</h3>
            </div>
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row">
                  <button type="button" class="jwx-table-view__action-row" @click="pushView('frequency')">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Frequency</p>
                    <div class="jwx-table-view__action-row-detail">
                      <span class="jwx-table-view__row-value">{{ frequencyLabels[frequency] }}</span>
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
                <li class="jwx-table-view__row">
                  <button type="button" class="jwx-table-view__action-row" @click="pushView('day')">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Day</p>
                    <div class="jwx-table-view__action-row-detail">
                      <span class="jwx-table-view__row-value">{{ selectedDaysLabel }}</span>
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <!-- Observation Settings -->
          <div v-if="sendOnObservations" class="jwx-modal__section">
            <div class="jwx-modal__section-header">
              <h3 class="jwx-modal__section-title">Observations Settings</h3>
            </div>
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row" data-multi-row>
                  <div class="jwx-table-view__row-content">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Observation Window</p>
                    <span class="jwx-table-view__row-value">{{ timeRangeLabel }}</span>
                  </div>
                  <div class="jwx-table-view__row-content">
                    <RangeSliderDual
                      id="wind-alert-time-range-slider"
                      :min="0"
                      :max="24"
                      v-model:minModel="startHour"
                      v-model:maxModel="endHour"
                    />
                  </div>
                </li>
              </ul>
              <p class="jwx-modal__section-description">Only send alerts for observations recorded during these hours.</p>
            </div>
          </div>

          <!-- Section 5: Forecast Settings -->
          <div v-if="sendOnForecast" class="jwx-modal__section">
            <div class="jwx-modal__section-header">
              <h3 class="jwx-modal__section-title">Forecast Settings</h3>
            </div>
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row">
                  <button
                    type="button"
                    class="jwx-table-view__action-row"
                    @click="pushView('forecastModels')"
                  >
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Forecast Models</p>
                    <div class="jwx-table-view__action-row-detail">
                      <span class="jwx-table-view__row-value">{{ selectedModelsLabel }}</span>
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
                <li class="jwx-table-view__row">
                  <button
                    type="button"
                    class="jwx-table-view__action-row"
                    @click="pushView('modelRange')"
                  >
                    <p class="jwx-table-view__row-label jwx-font-weight-400">Model Range</p>
                    <div class="jwx-table-view__action-row-detail">
                      <span class="jwx-table-view__row-value">{{ modelRangeLabels[modelRange] }}</span>
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <!-- View Recent Alerts (edit mode only) -->
          <div v-if="isEditMode" class="jwx-modal__section">
            <div class="jwx-modal__section-body">
              <ul class="jwx-block jwx-table-view" data-surface="1">
                <li class="jwx-table-view__row">
                  <button type="button" class="jwx-table-view__action-row" @click="pushView('history')">
                    <p class="jwx-table-view__row-label jwx-font-weight-400">View Recent Alerts</p>
                    <div class="jwx-table-view__action-row-detail">
                      <Icon name="chevron-right" />
                    </div>
                  </button>
                </li>
              </ul>
            </div>
          </div>

        </div>

        <!-- Detail views -->
        <ModalWindAlertManageDetailViewTriggerOn v-else-if="activeView === 'triggerOn'" v-model="triggerOn" />
        <ModalWindAlertManageDetailViewFrequency v-else-if="activeView === 'frequency'" v-model="frequency" />
        <ModalWindAlertManageDetailViewModelRange v-else-if="activeView === 'modelRange'" v-model="modelRange" />
        <ModalWindAlertManageDetailViewDay v-else-if="activeView === 'day'" v-model="selectedDays" />
        <ModalWindAlertManageDetailViewForecastModels v-else-if="activeView === 'forecastModels'" v-model="selectedModelIds" :models="availableModels" :loading="modelsLoading" />
        <ModalWindAlertManageDetailViewHistory
          v-else-if="activeView === 'history'"
          :alertId="historyAlertId"
        />
        <AlertDevicesManageContent
          v-else-if="activeView === 'devices'"
          ref="devicesContentRef"
          @saved="windAlertStore.getAlertDevices(undefined, true)"
        />

      </Transition>
    </div>

    <div class="jwx-modal__footer">
      <div class="jwx-modal__actions">
        <button
          v-if="devicesViewActive && !devicesContentAtRoot"
          type="button"
          @click.prevent="handleDevicesSubmit"
          class="jwx-button"
        >
          {{ devicesContentSubmitLabel }}
          <span v-if="devicesContentErrorCount > 0" class="jwx-button__error-pill">{{ devicesContentErrorCount }} {{ devicesContentErrorCount === 1 ? 'Error' : 'Errors' }}</span>
        </button>
        <button
          v-else-if="!devicesViewActive"
          type="button"
          @click.prevent="handleSubmit"
          class="jwx-button"
        >
          {{ actionLabel }}
          <span v-if="errorCount > 0" class="jwx-button__error-pill">{{ errorCount }} {{ errorCount === 1 ? 'Error' : 'Errors' }}</span>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<style lang="scss">
  .jwx-alert-row__device-disabled-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-inline-end: 2px;
    color: var(--jwx-text-color-subtle);

    svg {
      width: 24px;
      height: 24px;
      fill: var(--jwx-text-color-subtle);
    }
  }
</style>
